import React, { useEffect, useState } from "react";
import {
  Button,
  FunctionField,
  ReferenceField,
  ShowProps,
  useDataProvider,
  useNotify,
} from "react-admin";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { GridinRowsWithSpace } from "utils/style";
import EditIcon from "@material-ui/icons/Edit";
import CustomEmptyComponent from "../../../components/CustomEmptyComponent";
import { GetParam, StudentResources } from "utils/tools";
import moment from "moment/moment";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CustomLabeled from "../../../components/layout/CustomLabeled";
import { fetchAllData } from "../../../dataProvider";
import { isEmpty } from "lodash";

const useStyles = makeStyles({
  accordionLabel: {
    marginTop: "5px",
  },
  MuiAccordionSummary: {
    "& .MuiAccordionSummary-content": {
      margin: "15px 0px 5px",
    },
  },
  formControlContainer: {
    display: 'flex',
    gap: '1rem',
  },
  academicYearField: {
    minWidth: 160,
    flex: '1 1 auto',
  },
  termScheduleField: {
    minWidth: 300,
    flex: '2 1 auto',
  },
});

interface IStudentScheduleWithTerm extends IStudentSchedule {
  key: string;
  termName: string;
}

const StudentSubsidyTable = (_props: ShowProps) => {
  const classes = useStyles();
  const provider = useDataProvider();
  const notify = useNotify();
  const param = GetParam();
  const { id } = _props;
  const dateFormat = "DD / MM / YYYY";

  const studentResources = StudentResources(Number(id));
  const [studentSubsidy, setStudentSubsidy] = useState<IStudentSubsidy>();
  const [schoolMaxStudentSubsidy, setSchoolMaxStudentSubsidy] = useState<number>(null);
  const [studentSchedules, setStudentSchedules] = useState<IStudentScheduleWithTerm[]>([]);
  const [academicYears, setAcademicYears] = useState<IAcademicYear[]>([]);
  const [academicTerms, setAcademicTerms] = useState<Record<number, string>>({});
  const [academicYearId, setAcademicYearId] = useState<number>();
  const [studentScheduleKey, setStudentScheduleKey] = useState<string>();
  const [estimateStudentSubsidy, setEstimateStudentSubsidy] = useState<number>();

  useEffect(() => {
    fetchAllData(
      "academicYears",
      {
        SchoolId: Number(studentResources.schoolId),
      },
      {
        field: "id",
        order: "desc",
      },
    ).then((result) => {
      setAcademicYears(result as IAcademicYear[]);

      // The default is active AcademicYearId
      // If there is not active AcademicYearId,
      // I assume the default is latest AcademicYearId
      const activeAcademicYear = result.find(
        (item) => item.status.toLowerCase() === "active",
      );

      if (activeAcademicYear) {
        setAcademicYearId(activeAcademicYear.id as number);
      } else {
        if (result.length > 0) {
          setAcademicYearId(result[0].id as number);
        }
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (studentResources.activeStudentSubsidyId) {
      provider
        .getOne(`studentSubsidies`, {
          id: studentResources.activeStudentSubsidyId,
        })
        .then(({ data }) => {
          setStudentSubsidy(data as IStudentSubsidy);
        });

      provider
        .getOne(`schools`, {
          id: studentResources.schoolId,
        })
        .then(({ data }) => {
          setSchoolMaxStudentSubsidy(data?.maximumStudentSubsidy || null);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Get academic terms when academicYearId changes
  // Only run when academicYearId is set (not null/undefined)
  useEffect(() => {
    if (academicYearId) {
      fetchAllData(
        "academicTerms",
        {
          AcademicYearId: Number(academicYearId),
        },
        {
          field: "Id",
          order: "asc",
        },
      ).then((result) => {
        // Map array of academic terms into an object:
        // { [termId]: termName } for faster lookup later
        const mapped = Object.fromEntries(
          result.map((academicTerm: IAcademicTerm) => [
            academicTerm.id,
            academicTerm.name,
          ])
        );

        setAcademicTerms(mapped);
      });
    }
  }, [academicYearId]);

  // Get student schedules after academic terms are loaded
  // Validate:
  // - academicYearId must be defined
  // - id (student ID) must be defined
  // - academicTerms must not be empty (meaning Effect 1 has finished loading)
  useEffect(() => {
    if (academicYearId && id && !isEmpty(academicTerms ?? {})) {
      fetchAllData(
        "studentSchedules",
        {
          StudentId: Number(id),
          AcademicYearId: Number(academicYearId),
        },
        {
          field: "StartedAt",
          order: "asc",
        },
      ).then((result) => {
        const schedules = result as IStudentSchedule[];

        // Filter out schedules where the student is not taking
        // and build a new array with an additional `key` and `termName` for display
        const schedulesWithKey = schedules
          .filter((schedule) => schedule.isTaking)
          .map((schedule, index) => ({
          ...schedule,
          key: `${schedule.termId}-${index}`, // unique key for rendering and selection
          termName: academicTerms[schedule.termId] || "", // lookup term name from academicTerms
        }));
        setStudentSchedules(schedulesWithKey as IStudentScheduleWithTerm[]);

        // Automatically select the first schedule (if any)
        if (schedulesWithKey.length > 0) {
          setStudentScheduleKey(schedulesWithKey[0].key);
        } else {
          setStudentScheduleKey(null);
        }
      });
    } else {
      setStudentScheduleKey(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicTerms, id]);

  // Calculate the estimate subsidy for selected academic year and schedule
  // only run if academicYearId and selected schedule key are available
  useEffect(() => {
    if (academicYearId && studentScheduleKey && studentSubsidy) {
      // Find the selected schedule data based on the selected key
      const selectedSchedule = studentSchedules.find(
        (s) => s.key === studentScheduleKey
      );

      // If no matching schedule found, set the subsidy estimate to null
      if (!selectedSchedule) {
        setEstimateStudentSubsidy(null);
        return;
      }

      // Call the Cost Preview API to estimate the cost/subsidy for the selected schedule
      provider
        .create("CostPreview/registerStudent", {
          data: {
            isRegisterRegularSchedule: true,
            schoolId: studentResources.schoolId,
            regularStudentSchedule: {
              academicYearId,
              startedAt: selectedSchedule.startDate,
              isTaking: true,
              detail: {
                lat: selectedSchedule.detail.lat,
                lng: selectedSchedule.detail.lng,
                street: selectedSchedule.detail.street,
                unitNumber: selectedSchedule.detail.unitNumber
                  ? selectedSchedule.detail.unitNumber
                  : null,
                blockNumber: selectedSchedule.detail.blockNumber,
                postalCode: selectedSchedule.detail.postalCode,
                building: selectedSchedule.detail.building,
                isMonday: selectedSchedule.detail.isMonday,
                isTuesday: selectedSchedule.detail.isTuesday,
                isWednesday: selectedSchedule.detail.isWednesday,
                isThursday: selectedSchedule.detail.isThursday,
                isFriday: selectedSchedule.detail.isFriday,
                isFromSchool: selectedSchedule.detail.isFromSchool,
                isToSchool: selectedSchedule.detail.isToSchool,
              },
              isApplyStudentSubsidy: true,
              studentSubsidy: {
                periodStartDate: studentSubsidy.periodStartDate,
                periodEndDate: studentSubsidy.periodEndDate,
                subsidyPercentage: studentSubsidy.subsidyPercentage,
              },
            },
          }
        })
        .then((response: any) => {
          if (response?.res?.json) {
            const dataResponse = response?.res?.json;

            // Check if there's a regular schedule cost preview response containing transactions
            // Because currently, only regular schedule can has subsidy
            if (dataResponse?.updateRegularScheduleResponse.length > 0) {
              // Flatten all transactions from responses and find subsidy transaction
              const subsidyTransaction = dataResponse?.updateRegularScheduleResponse
                .flatMap((item: any) => item.transactions) // merge all transactions into one array
                .find((trx: any) => trx.type === "subsidy"); // find the subsidy transaction

              // If subsidy transaction exists, set subsidy amount (convert to positive number)
              // Otherwise set the subsidy preview tu null
              if (subsidyTransaction) {
                setEstimateStudentSubsidy(subsidyTransaction.total * -1);
              } else {
                setEstimateStudentSubsidy(null);
              }
            }
          }
        })
        .catch((err) => {
          notify(err.message);
          setEstimateStudentSubsidy(null);
        });
    } else {
      // If academicYearId or selected schedule key is missing, set the subsidy estimate to null
      setEstimateStudentSubsidy(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearId, studentScheduleKey, studentSubsidy]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="split-payment-content"
        id="student-subsidy-header"
        className={classes.MuiAccordionSummary}
      >
        <h3 className={classes.accordionLabel}>Student Subsidy</h3>
      </AccordionSummary>
      <AccordionDetails>
        <SanitizedGrid container spacing={2}>
          {studentSubsidy && (
            <Grid item xs={12} style={GridinRowsWithSpace}>
              <Typography variant="subtitle1">Student Subsidy Detail</Typography>
              <span>
                <Button
                  onClick={() =>
                    _props.history.push(
                      `/studentSubsidies/${studentSubsidy.id}?studentId=${id}${
                        param.get("parentId")
                          ? `&parentId=${param.get("parentId")}`
                          : ""
                      }`,
                    )
                  }
                  label="Edit"
                >
                  <EditIcon />
                </Button>
                <Button
                  onClick={() =>
                    _props.history.push(
                      `/studentSubsidies?studentId=${id}${
                        param.get("parentId")
                          ? `&parentId=${param.get("parentId")}`
                          : ""
                      }`,
                    )
                  }
                  label="Show History"
                >
                  <VisibilityIcon />
                </Button>
              </span>
            </Grid>
          )}

          {studentSubsidy ? (
            <>
              <Grid item xs={12}>
                <CustomLabeled name="Period">
                  <Typography variant="body2">
                    {
                      (studentSubsidy?.periodStartDate === undefined ||
                        studentSubsidy?.periodStartDate === null) &&
                      (studentSubsidy?.periodEndDate === undefined ||
                        studentSubsidy?.periodEndDate === null)
                      ? `-`
                      : `${moment
                        .utc(studentSubsidy?.periodStartDate)
                        .format(dateFormat)} - ${moment
                        .utc(studentSubsidy?.periodEndDate)
                        .format(dateFormat)}`
                    }
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Subsidy Percentage">
                  <Typography variant="body2">
                    {studentSubsidy?.subsidyPercentage === undefined ||
                    studentSubsidy?.subsidyPercentage === null
                      ? `-`
                      : `${studentSubsidy?.subsidyPercentage}%`}
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Max Bus Fare Cap">
                  <Typography variant="body2">
                    {schoolMaxStudentSubsidy === undefined ||
                    schoolMaxStudentSubsidy === null
                      ? `-`
                      : `$ ${(schoolMaxStudentSubsidy / 100).toFixed(2)}`}
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Remark">
                  <Typography variant="body2">
                    {studentSubsidy?.remark === undefined ||
                    studentSubsidy?.remark === null
                      ? `-`
                      : studentSubsidy?.remark}
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Last Updated At">
                  <Typography variant="body2">
                    {studentSubsidy?.updatedAt === undefined ||
                    studentSubsidy?.updatedAt === null
                      ? `-`
                      : moment
                        .utc(studentSubsidy?.updatedAt)
                        .format("DD / MM / YYYY, hh:mm A")}
                  </Typography>
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Last Updated By">
                  {studentSubsidy.updatedBy === null ? (
                    "-"
                  ) : (
                    <ReferenceField
                      reference="admins"
                      source="updatedBy.id"
                      linkType={false}
                      record={studentSubsidy}
                    >
                      <FunctionField
                        render={(record: any) =>
                          `${studentSubsidy.updatedBy.role} - ${record.fullName}`
                        }
                      />
                    </ReferenceField>
                  )}
                </CustomLabeled>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Calculate Student Subsidy Estimate</Typography>
              </Grid>

              <Grid item xs={6}>
                <Alert severity="info">
                  Calculation is based on the student schedule. It calculate between
                  the overlapping date of the rate chart price and the schedule date.
                  The price is using the first month of the term based on the rate chart.
                </Alert>
              </Grid>
              <Grid item xs={6}></Grid>

              <Grid item xs={6} className={classes.formControlContainer}>
                <FormControl variant="filled" className={classes.academicYearField}>
                  <InputLabel id="academicYear">Academic Year</InputLabel>
                  {academicYearId && (
                    <Select
                      labelId="academicYear"
                      id="academicYear"
                      value={academicYearId}
                      onChange={(event) => {
                        setAcademicYearId(Number(event.target.value));
                        setStudentScheduleKey(null);
                      }}
                      disabled={!academicYearId}
                    >
                      {academicYears.map((value: any) => {
                        return (
                          <MenuItem value={value.id} key={value.id}>
                            {value.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                </FormControl>

                <FormControl variant="filled" className={classes.termScheduleField}>
                  <InputLabel id="scheduleTerm">Schedule Term</InputLabel>
                  {studentScheduleKey && (
                    <Select
                      labelId="scheduleTerm"
                      id="scheduleTerm"
                      value={studentScheduleKey}
                      onChange={(event) => {
                        setStudentScheduleKey(event.target.value as string);
                      }}
                      disabled={!studentScheduleKey}
                    >
                      {studentSchedules.map((value: any) => {
                        return (
                          <MenuItem value={value.key} key={value.key}>
                            {value.termName} ({moment
                            .utc(value.startDate)
                            .format(dateFormat)} - {moment
                            .utc(value.endDate)
                            .format(dateFormat)})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <CustomLabeled name="Estimated Student Subsidy">
                  <Typography variant="body2">
                    {estimateStudentSubsidy === undefined ||
                    estimateStudentSubsidy === null
                      ? `-`
                      : `$ ${(estimateStudentSubsidy / 100).toFixed(2)}`}
                  </Typography>
                </CustomLabeled>
              </Grid>
            </>
          ) : (
            <CustomEmptyComponent
              resource={`studentSubsidies`}
              optionalQuery={`?source=${JSON.stringify({
                studentId: Number(id),
              })}&studentId=${id}${
                param.get("parentId")
                  ? `&parentId=${param.get("parentId")}`
                  : ""
              }`}
              hasCreate={true}
            />
          )}
        </SanitizedGrid>
      </AccordionDetails>
    </Accordion>
  );
};

export default StudentSubsidyTable;
